import styled from "@emotion/styled";
import { NATIVE_SOL, STEP_MINT, WRAPPED_SOL_MINT } from "@stepfinance/common";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CellDefault,
  formatBalance,
  formatPercentage,
  getColumnSearchProps,
  StepButtonDropdown,
  StepTable,
  TagInfo,
  TagInfoContainer,
} from "@stepfinance/react";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { FilterValue, TablePaginationConfig } from "antd/lib/table/interface";
import type { Dispatch, FC } from "react";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { useNavigationContext } from "../../../contexts/navigation";
import { useSwapContextState } from "../../../contexts/swap";
import { useCalculatePriceForDisplayCurrencyNumeric } from "../../../hooks";
import { getTokenName } from "../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { NavigationLink, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import type { StakeActionOption } from "./Balances";
import { tokenSymbolSearchGetter } from "./Balances";

interface SpotAssetsProps {
  userTokens: NormalizedTokenAccount[];
  spotAssetsBalanceUSD: number | null;
  setSendAccount: Dispatch<React.SetStateAction<NormalizedTokenAccount | null>>;
  setBurnAccount: Dispatch<React.SetStateAction<NormalizedTokenAccount | null>>;
  setStakingOption: Dispatch<React.SetStateAction<StakeActionOption | null>>;
}

export const SpotAssets: FC<SpotAssetsProps> = ({
  userTokens,
  spotAssetsBalanceUSD,
  setSendAccount,
  setStakingOption,
  setBurnAccount,
}) => {
  const { navigate } = useNavigationContext();
  const { connected, watching } = useEnrichedWallet();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "spotAssetsPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const { marketDataMap } = useMarkets();
  const calculatePriceForDisplayCurrencyNumeric = useCalculatePriceForDisplayCurrencyNumeric();

  const {
    currentLanguageData: {
      common,
      components: { table: tableStrings },
      pages: {
        dashboard: {
          modules: { balances },
        },
      },
    },
  } = useLanguageContext();

  const { A: swapEntryA, B: swapEntryB } = useSwapContextState();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(userTokens.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [userTokens.length, paginationSetting]);

  const columns: StepColumnsType<NormalizedTokenAccount> = [
    {
      title: common.asset,
      dataIndex: "symbol",
      key: "symbol",
      width: 272,
      render(_, record) {
        const {
          info: { mint },
        } = record;
        return (
          <AssetCell>
            <TokenIconContainer>
              <TokenIcon mintAddress={mint} />
              <TokenIconText bold>
                <NavigationLink href={`https://analytics.step.finance/defionsolana/token?mint=${mint}`} target="_blank">
                  {getTokenName(mint)}
                </NavigationLink>
              </TokenIconText>
            </TokenIconContainer>
            <StepButtonDropdown
              buttonTitle={balances.sendButton}
              onButtonClick={() => setSendAccount(record)}
              items={[
                {
                  label: balances.sendButton,
                  hidden: !(connected && !watching),
                  onClick: () => setSendAccount(record),
                },
                {
                  label: common.stake,
                  hidden: !(connected && !watching && mint === NATIVE_SOL.toBase58()),
                  onClick: () => {
                    swapEntryA.setAmount("");
                    setStakingOption({ action: "stake", option: "Marinade" });
                  },
                },
                {
                  label: balances.spotAssets.unwrapButton,
                  hidden: !(connected && !watching && mint === WRAPPED_SOL_MINT.toBase58()),
                  onClick: (event) => {
                    event.stopPropagation();
                    swapEntryA.setAmount("");
                    swapEntryB.setMint(NATIVE_SOL);
                    navigate(`/swap?a=${WRAPPED_SOL_MINT}&b=${NATIVE_SOL}`);
                  },
                },
                {
                  label: balances.spotAssets.wrapButton,
                  hidden: !(connected && !watching && mint === NATIVE_SOL.toBase58()),
                  onClick: (event) => {
                    event.stopPropagation();
                    swapEntryA.setAmount("");
                    swapEntryA.setMint(NATIVE_SOL);
                    navigate(`/swap?a=${NATIVE_SOL}&b=${WRAPPED_SOL_MINT}`);
                  },
                },
                {
                  label: common.stake,
                  hidden: !(connected && !watching && mint === STEP_MINT.toBase58()),
                  onClick: (event) => {
                    event.stopPropagation();
                    swapEntryA.setAmount("");
                    navigate(`/stake`);
                  },
                },
                {
                  label: common.burnToken,
                  hidden: !(
                    connected &&
                    !watching &&
                    mint !== NATIVE_SOL.toBase58() &&
                    mint !== WRAPPED_SOL_MINT.toBase58()
                  ),
                  onClick: () => setBurnAccount(record),
                },
              ]}
            />
          </AssetCell>
        );
      },
      ...getColumnSearchProps(tokenSymbolSearchGetter, "asset"),
      filteredValue: filteredInfo.symbol ?? null,
      sorter: (a, b) => {
        let strA = getTokenName(a.info.mint);
        let strB = getTokenName(b.info.mint);
        if (strA === undefined) {
          strA = a.info.mint;
        }
        if (strB === undefined) {
          strB = b.info.mint;
        }
        return strA.localeCompare(strB);
      },
    },

    {
      title: common.amount,
      dataIndex: "balance",
      key: "balance",
      className: "column-number",
      render(_, record) {
        return (
          <CellValue cellType="number">
            {formatBalance(record.tokenAmount.uiAmount, record.tokenAmount.decimals)}
          </CellValue>
        );
      },
      sorter: (a, b) => a.tokenAmount.uiAmount - b.tokenAmount.uiAmount,
    },
    {
      title: tableStrings.headers.price24h,
      dataIndex: "tokenPrice",
      key: "tokenPrice",
      className: "column-number",
      render(_, record) {
        const {
          tokenAmount: { priceUSD },
        } = record;

        if (priceUSD === null) {
          return (
            <PriceCell>
              <CellDefault />
            </PriceCell>
          );
        }

        const change24hPrice = calculatePriceForDisplayCurrencyNumeric(
          marketDataMap.get(record.info.mint)?.change24h ?? 0,
        );
        // If the coin has no track of its price 24 hrs ago, this field will be 0
        const hasNoPriorRecord = change24hPrice === 0;
        const currentPrice = calculatePriceForDisplayCurrencyNumeric(priceUSD);
        const change24hPct = (currentPrice - change24hPrice) / change24hPrice;

        return (
          <PriceCell>
            <CellValue cellType="value">{priceUSD}</CellValue>
            {!hasNoPriorRecord && (
              <TagInfoContainer>
                <TagInfo tagType="performance" performanceNumber={change24hPct}>
                  {formatPercentage(change24hPct, { capped: true, performance: true })}
                </TagInfo>
              </TagInfoContainer>
            )}
          </PriceCell>
        );
      },
      sorter: (a, b) => (a.tokenAmount.priceUSD ?? 0) - (b.tokenAmount.priceUSD ?? 0),
    },
    {
      title: common.value,
      dataIndex: "balanceInUSD",
      key: "balanceInUSD",
      className: "column-number",
      render(_, record) {
        const {
          tokenAmount: { balanceInUSD },
        } = record;

        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {balanceInUSD}
          </CellValue>
        );
      },
      sorter: (a, b) => {
        if ((a.tokenAmount.balanceInUSD ?? 0) - (b.tokenAmount.balanceInUSD ?? 0) === 0) {
          return a.tokenAmount.uiAmount - b.tokenAmount.uiAmount;
        }
        return (a.tokenAmount.balanceInUSD ?? 0) - (b.tokenAmount.balanceInUSD ?? 0);
      },
      defaultSortOrder: "descend",
    },
  ];

  return userTokens.length > 0 ? (
    <SpotAssetContainer>
      <ChevronCollapse isCollapseChild title={balances.spotAssets.title} value={spotAssetsBalanceUSD}>
        <StepTable<NormalizedTokenAccount>
          columns={columns}
          dataSource={userTokens}
          rowKey={(record) => record.info.mint}
          pagination={pagination}
          onChange={(pagination, filters) => {
            setPagination(pagination);
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          loading={!userTokens?.length}
        />
      </ChevronCollapse>
    </SpotAssetContainer>
  ) : (
    <></>
  );
};

const PriceCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const AssetCell = styled.div`
  display: flex;
  align-items: center;
`;

export const TokenIconContainer = styled.div`
  margin-right: 80px;
  width: 100px;
  display: flex;
  align-items: center;
`;

const SpotAssetContainer = styled.div`
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;
